import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { CampaignType } from 'modules/domains/campaign';
import {
  FullWidthDisabledTooltip,
  TooltipContentOnTopOfPopperMenu,
} from 'modules/experiments/components/pages/CommonStyled';
import {
  SEND_TIME_MEDIUMS,
  STATES_WITH_DELETE_ACTION,
  STO_MEDIUMS,
} from 'modules/experiments/constants';
import {
  BasePageEmailContentDefinitionProperties,
  BasePageExperimentMediumContent,
  EXPERIMENTS_TABLE_COLUMNS,
  ExperimentTableData,
  ExperimentsTableQuery,
  ExperimentsTableQueryInput,
} from 'modules/experiments/types';
import {
  EmailContentDefinition,
  ExperimentState,
  ExperimentType,
  MessageMedium,
} from 'modules/graphql/types';
import { TFunction } from 'react-i18next';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { Delete, Edit16 } from 'ui/icons';
import { NoOp } from 'utils/noop';
import { Nullable } from 'utils/tsHelpers';
import { JSXToText } from './common';
import {
  getFormattedDateFromISOZonedDate,
  getFormattedDateFromISOZonedDateWithTimeZone,
} from './date';
import { getExperimentStatesWithEditAction } from './ruleEngine';
import { determineExperimentState } from './state';
import { selectedFiltersToTableFilterInfo } from './tableFilter';

type GetExperimentActionMenuItemsArgs = {
  viewAndDraftPermission: boolean;
  campaignType: CampaignType;
  status: ExperimentState;
  experimentLinkAttributes: React.AnchorHTMLAttributes<HTMLAnchorElement>;
  t: TFunction;
  showDeleteModal: NoOp;
};

export const getExperimentColumnData = (t: TFunction) => [
  {
    id: EXPERIMENTS_TABLE_COLUMNS.NAME,
    label: t('table.header.name'),
    isSortable: true,
    initialSortDirection: SORT_DIRECTIONS.ASCENDING,
    isRightAligned: false,
  },
  {
    id: EXPERIMENTS_TABLE_COLUMNS.SUBJECT,
    label: t('table.header.subject'),
    isSortable: true,
    initialSortDirection: SORT_DIRECTIONS.ASCENDING,
    isRightAligned: false,
  },
  {
    id: EXPERIMENTS_TABLE_COLUMNS.CONVERSION_WINNER,
    label: t('table.header.conversionWinner'),
    isSortable: false,
    initialSortDirection: SORT_DIRECTIONS.ASCENDING,
    isRightAligned: false,
  },
  {
    id: EXPERIMENTS_TABLE_COLUMNS.STATUS,
    label: t('table.header.status'),
    isSortable: true,
    initialSortDirection: SORT_DIRECTIONS.ASCENDING,
    isRightAligned: false,
  },
  {
    id: EXPERIMENTS_TABLE_COLUMNS.START_DATE,
    label: t('table.header.startDate'),
    isSortable: true,
    initialSortDirection: SORT_DIRECTIONS.ASCENDING,
    isRightAligned: false,
  },
  {
    id: EXPERIMENTS_TABLE_COLUMNS.END_DATE,
    label: t('table.header.endDate'),
    isSortable: true,
    initialSortDirection: SORT_DIRECTIONS.ASCENDING,
    isRightAligned: false,
  },
  {
    id: EXPERIMENTS_TABLE_COLUMNS.LAST_MODIFIED,
    label: t('table.header.lastModified'),
    isSortable: true,
    initialSortDirection: SORT_DIRECTIONS.ASCENDING,
    isRightAligned: false,
  },
];

export const getExperimentActionMenuItems = ({
  viewAndDraftPermission,
  campaignType,
  status,
  experimentLinkAttributes,
  t,
  showDeleteModal,
}: GetExperimentActionMenuItemsArgs): ActionMenuOption[] => {
  const isEditDisabled = !getExperimentStatesWithEditAction(campaignType).includes(status);
  const isDeleteDisabled = !STATES_WITH_DELETE_ACTION.includes(status);
  const { onClick, href } = experimentLinkAttributes;

  const deleteContent = (
    <>
      <Delete height={16} />
      <span data-test="delete-action-trigger">{t('table.actions.delete')}</span>
    </>
  );

  const deleteOption = !viewAndDraftPermission
    ? {
        id: 'delete',
        as: AllowedActionMenuElements.Button,
        content: deleteContent,
        onClick: showDeleteModal,
      }
    : {
        id: 'delete',
        isDisabled: true,
        as: AllowedActionMenuElements.Button,
        content: deleteContent,
        Wrapper: ({ children }: { children?: React.ReactNode }) => (
          <FullWidthDisabledTooltip
            tooltipContent={t('permissions.deleteExperiment')}
            ContentComponent={TooltipContentOnTopOfPopperMenu}
          >
            {children}
          </FullWidthDisabledTooltip>
        ),
      };

  const editOption = {
    as: AllowedActionMenuElements.Link,
    content: (
      <>
        <Edit16 />
        <span data-test="edit-action-trigger">
          <span>{t('table.actions.edit')}</span>
        </span>
      </>
    ),
    id: 'edit',
    href,
    onClick: onClick as NoOp,
  };

  return [...(isEditDisabled ? [] : [editOption]), ...(isDeleteDisabled ? [] : [deleteOption])];
};

const propertyExists = (
  obj: BasePageExperimentMediumContent,
  key: string,
  defaultValue: JSX.Element | string,
) =>
  key in obj && obj[key as keyof BasePageExperimentMediumContent]
    ? obj[key as keyof BasePageExperimentMediumContent]
    : defaultValue;

const formatFromNameAndSender = (
  t: TFunction,
  content: EmailContentDefinition | BasePageEmailContentDefinitionProperties,
  formatted: boolean,
) => {
  const name = content.fromName || t('notSet.fromName');
  const email = content.fromEmail || t('notSet.fromEmail');
  const testSubject = `${name} <${email}>`;
  return formatted ? (
    <>
      {name}&nbsp;{'<'}
      {email}
      {'>'}
    </>
  ) : (
    testSubject
  );
};

type TestingSubjectTextArgs = {
  formatted?: boolean;
  content: BasePageExperimentMediumContent;
  sendTime: Nullable<string>;
  sendTimeUseRecipientTimeZones: boolean;
  messageMedium: MessageMedium;
  control: boolean;
  type: ExperimentType;
  t: (key: string) => JSX.Element | string;
};

export const getTestingSubjectText = ({
  formatted = true,
  content,
  sendTime,
  sendTimeUseRecipientTimeZones,
  messageMedium,
  control,
  type,
  t,
}: TestingSubjectTextArgs) => {
  const isEmail = messageMedium === MessageMedium.Email;
  const isSendTimeMedium = SEND_TIME_MEDIUMS.includes(messageMedium);
  const isSTOMedium = STO_MEDIUMS.includes(messageMedium);

  switch (true) {
    case ExperimentType.SubjectLine === type && isEmail:
      return propertyExists(content, 'subject', t('notSet.subject'));
    case ExperimentType.PreheaderText === type && isEmail:
      return propertyExists(content, 'preheaderText', t('notSet.preheaderText'));
    case ExperimentType.FromNameAndSender === type && isEmail: {
      const fromNameOrEmailExists =
        'fromName' in content &&
        'fromEmail' in content &&
        (!!content.fromName || !!content.fromEmail);

      if (fromNameOrEmailExists) {
        return formatFromNameAndSender(t, content, formatted);
      }
      return t('notSet.fromNameSender');
    }
    case ExperimentType.SendTimeOptimization === type && isSTOMedium: {
      if (!sendTime && !control) return t('notSet.stoVariant');
      if (sendTime) {
        return getFormattedDateFromISOZonedDateWithTimeZone(sendTime);
      }
      return t('notSet.sendTime');
    }
    case ExperimentType.SendTime === type && isSendTimeMedium: {
      if (sendTime) {
        // We sometimes italicize all our translations. This will remove
        // all formatting style from the text.
        const recipientTZText = JSXToText(t('placeholder.recipient'));

        return sendTimeUseRecipientTimeZones
          ? `${getFormattedDateFromISOZonedDate(sendTime)} (${recipientTZText})`
          : getFormattedDateFromISOZonedDateWithTimeZone(sendTime);
      }
      return t('notSet.sendTime');
    }
    default:
      return t('placeholder.testSubject');
  }
};

export const getQueryVariables = ({
  selectedFilters,
  selectedTab,
  pageSize,
  page,
  sortBy,
  sortDirection,
  search,
}: ExperimentsTableQueryInput): ExperimentsTableQuery => {
  const queryVariables = {
    filterInfo: selectedFiltersToTableFilterInfo(selectedFilters, selectedTab),
    pagination: {
      limit: pageSize,
      offset: (page - 1) * pageSize,
    },
    sort: {
      by: sortBy,
      direction: sortDirection,
    },
    search,
  };
  return queryVariables;
};

export const serializeExperiments = (experiments: ExperimentTableData[]) =>
  experiments.map((exp) => ({
    ...exp,
    state: determineExperimentState(exp),
  }));
